import { useQuery } from "@/api/utils/useQuery"
import { QueryResult } from "@/api/utils/types"
import { HasuraQueries } from "@/api/hasura/queries"
import {
  IEmailTemplate,
  IEmailTemplateSummary,
  ICommunication,
} from "@/models/admin/emailTemplates/emailTemplates.types"
import {
  EmailTemplateParser,
  EmailTemplateSummaryParser,
  CommunicationParser,
} from "@/models/admin/emailTemplates/emailTemplates.parser"
import {
  IExternalEmailTemplate,
  IExternalEmailTemplateSummary,
} from "@/models/admin/emailTemplates/externalEmailTemplates.types"
import {
  ExternalEmailTemplateParser,
  ExternalEmailTemplateSummaryParser,
} from "@/models/admin/emailTemplates/externalEmailTemplates.parser"

export interface IEmailTemplatesService {
  getEmailTemplateSummaries(): QueryResult<IEmailTemplateSummary[]>
  getEmailTemplatesByCommunication(
    communicationId: number,
  ): QueryResult<IEmailTemplate[]>
  getCommunications(): QueryResult<ICommunication[]>
  getExternalEmailTemplates(): QueryResult<IExternalEmailTemplateSummary[]>
  getExternalEmailTemplateById(
    id: number,
  ): QueryResult<IExternalEmailTemplate | null>
}

class EmailTemplatesServiceClass implements IEmailTemplatesService {
  getEmailTemplateSummaries(): QueryResult<IEmailTemplateSummary[]> {
    return useQuery({
      ...HasuraQueries.emailTemplates.summaries,
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data) =>
        data.email_templates.map((template) =>
          EmailTemplateSummaryParser.toDomain(template),
        ) as IEmailTemplateSummary[],
    })
  }

  getEmailTemplatesByCommunication(
    communicationId: number,
  ): QueryResult<IEmailTemplate[]> {
    return useQuery({
      ...HasuraQueries.emailTemplates.byCommunication(communicationId),
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data) =>
        data.email_templates.map((template) =>
          EmailTemplateParser.toDomain(template),
        ) as IEmailTemplate[],
    })
  }

  getCommunications(): QueryResult<ICommunication[]> {
    return useQuery({
      ...HasuraQueries.emailTemplates.communications,
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data) =>
        data.communications.map((communication) =>
          CommunicationParser.toDomain(communication),
        ) as ICommunication[],
    })
  }

  getExternalEmailTemplates(): QueryResult<IExternalEmailTemplateSummary[]> {
    return useQuery({
      ...HasuraQueries.emailTemplates.external,
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data) =>
        data.email_templates_ext.map((template) =>
          ExternalEmailTemplateSummaryParser.toDomain(template),
        ) as IExternalEmailTemplateSummary[],
    })
  }

  getExternalEmailTemplateById(
    id: number,
  ): QueryResult<IExternalEmailTemplate | null> {
    return useQuery({
      ...HasuraQueries.emailTemplates.externalById(id),
      placeholderData: null,
      useRawResponse: true,
      parserFn: (data) => {
        const [template] = data.email_templates_ext
        return template ? ExternalEmailTemplateParser.toDomain(template) : null
      },
    })
  }
}

export const EmailTemplatesService = new EmailTemplatesServiceClass()
